import { Injectable, NgZone, OnDestroy, computed, inject, signal } from '@angular/core';
import { DashboardStateService } from './dashboard-state.service';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class ViewRotationService implements OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly stateService = inject(DashboardStateService);

  readonly cycleTime = environment.dashboard.cycleTime;
  readonly progress = signal<number>(0);

  private readonly enabledIndices: number[] = Object.values(environment.dashboard.views)
    .filter(v => v.enabled)
    .sort((a, b) => a.index - b.index)
    .map(v => v.index);

  readonly enabledCount = this.enabledIndices.length;
  private readonly pointer = signal<number>(0);
  readonly manualOverride = signal<number | null>(null);
  readonly currentViewIndex = computed(() => {
    const manual = this.manualOverride();
    if (manual !== null) return manual;
    return this.enabledIndices.length > 0 ? this.enabledIndices[this.pointer()] : 0;
  });

  private cycleStartTime = Date.now();
  private animationIntervalId: ReturnType<typeof setInterval> | null = null;

  start(): void {
    if (this.enabledIndices.length === 0) return;

    this.ngZone.runOutsideAngular(() => {
      this.animationIntervalId = setInterval(() => {
        if (this.stateService.isShiftChanging()) return;
        if (this.manualOverride() !== null) return;

        const now = Date.now();
        const elapsed = now - this.cycleStartTime;
        const cycleProgress = (elapsed % this.cycleTime) / this.cycleTime * 100;

        this.ngZone.run(() => {
          this.progress.set(Math.min(cycleProgress, 100));

          if (elapsed >= this.cycleTime) {
            this.pointer.update((p) => (p + 1) % this.enabledIndices.length);
            this.cycleStartTime = now;
          }
        });
      }, 16);
    });
  }

  jumpTo(viewIndex: number): void {
    this.manualOverride.set(viewIndex);
    this.progress.set(0);
    const enabledPos = this.enabledIndices.indexOf(viewIndex);
    if (enabledPos !== -1) this.pointer.set(enabledPos);
  }

  resumeRotation(): void {
    this.manualOverride.set(null);
    this.cycleStartTime = Date.now();
  }

  ngOnDestroy(): void {
    if (this.animationIntervalId) {
      clearInterval(this.animationIntervalId);
    }
  }
}
